import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useFormatters } from '../../contexts/PreferencesContext'
import { useDrilldownAction } from '../drilldown'
import { ChartWrapper } from './ChartWrapper'

interface LegacyIncomingPerWeekChartProps {
  incomingByWeek: Array<{ week: string; value: number; lines?: number; arrears_value?: number; arrears_lines?: number; week_start?: string; week_end?: string }>
  scopeDrilldownFilters: Record<string, unknown>
  costLine: Record<string, unknown>
}

/**
 * The Cost tab's weekly chart as it was before CR-1421 (the twelve weeks up
 * to today, from ``dashboard_stats``' ``incoming_by_week``). Moved out of
 * CostTab unchanged: it is still what a schedule-line scope sees, because
 * schedule lines are left alone. PO-line scopes get ``CostWeeklyCharts``.
 */
export function LegacyIncomingPerWeekChart({ incomingByWeek, scopeDrilldownFilters, costLine }: LegacyIncomingPerWeekChartProps) {
  const { formatCurrency, formatCompactCurrency } = useFormatters()
  const { openDrilldown } = useDrilldownAction()

  // CR-3847 + CR-3925: Incoming Per Week by Latest Supplier Promise Date
  // Now includes all 4 metrics: arrears value, arrears lines, incoming value, incoming lines
  // CR-5455 rework: carry week_start/week_end through so the click handler
  // can scope the drill-down to the clicked bucket.
  const incomingByWeekPromiseDate = (incomingByWeek || []).map((w: { week: string; value: number; lines?: number; arrears_value?: number; arrears_lines?: number; week_start?: string; week_end?: string }) => ({
    week: w.week,
    incoming_value: w.value,
    incoming_lines: w.lines || 0,
    arrears_value: w.arrears_value || 0,
    arrears_lines: w.arrears_lines || 0,
    week_start: w.week_start,
    week_end: w.week_end,
  }))

  // CR-3966 / CR-5455 rework: dedicated click handler for the weekly chart.
  // QA flagged that this used to always open the generic 'arrears' metric
  // (wrong date field — delivery_date instead of the chart's own Latest
  // Supplier Promise Date — and no week scoping, so it returned an
  // unrelated ~6-month sample). Now opens 'incoming_per_week', scoped to
  // the clicked bar's own week_start/week_end, which filters on the same
  // effective date the chart buckets by.
  const handleWeekChartClick = (chartData: unknown) => {
    const data = chartData as { activePayload?: Array<{ payload?: { week?: string; week_start?: string; week_end?: string } }> }
    const payload = data?.activePayload?.[0]?.payload
    if (!payload) return
    // Both bounds or nothing. Spreading them conditionally meant a payload
    // missing either one opened an unscoped 'incoming_per_week' — every open
    // line, which is precisely the QA failure this ticket fixed. A click that
    // cannot be scoped to its own week does nothing instead.
    if (!payload.week_start || !payload.week_end) return
    openDrilldown(
      'incoming_per_week',
      {
        ...scopeDrilldownFilters,
        ...costLine,
        date_from: payload.week_start,
        date_to: payload.week_end,
      },
      payload.week ? `Incoming Per Week — ${payload.week}` : 'Incoming Per Week',
    )
  }

  // CR-3847 + CR-3925: Incoming Per Week by Latest Supplier Promise Date - Single combined chart
  return (
    <ChartWrapper
      title="Incoming Per Week by Latest Supplier Promise Date"
      data={incomingByWeekPromiseDate}
      exportFilename="incoming-by-week"
      legend={[
        { color: '#3b82f6', label: 'Total Incoming Value' },
        { color: '#ef4444', label: 'Arrears to Date Value' },
        { color: '#10b981', label: 'Total Incoming PO Lines' },
        { color: '#f59e0b', label: 'Arrears to Date PO Lines' },
      ]}
    >
      <div
        // Recharts 3 fires a chart-level onClick only when the pointer
        // is over a rendered element, and populates activePayload only
        // in that same case. A click in the gap between bars — or above
        // them — therefore does nothing, even though the tooltip names
        // the week. Resolve such clicks against the x-axis tick
        // geometry so anywhere in the plot area drills into the week
        // the user is pointing at.
        onClick={(e) => {
          if (document.querySelector('[role="dialog"]')) return;
          const host = e.currentTarget as HTMLElement;
          const ticks = Array.from(
            host.querySelectorAll<SVGGElement>('.recharts-xAxis .recharts-cartesian-axis-tick'),
          );
          if (!ticks.length || !incomingByWeekPromiseDate.length) return;
          let best = -1;
          let bestDist = Infinity;
          ticks.forEach((tick, i) => {
            const r = tick.getBoundingClientRect();
            const d = Math.abs(r.x + r.width / 2 - e.clientX);
            if (d < bestDist) { bestDist = d; best = i; }
          });
          const point = incomingByWeekPromiseDate[best];
          if (point) handleWeekChartClick({ activePayload: [{ payload: point }] });
        }}
        style={{ cursor: 'pointer' }}
      >
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart
          data={incomingByWeekPromiseDate}
          onClick={handleWeekChartClick}
          style={{ cursor: 'pointer' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="value" tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompactCurrency(value)} />
          <YAxis yAxisId="lines" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip
            cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
            formatter={(value: number, name: string) =>
              name.includes('Value') ? formatCurrency(value) : `${value} lines`
            }
          />
          <Bar yAxisId="value" dataKey="incoming_value" fill="#3b82f6" name="Total Incoming Value" radius={[4, 4, 0, 0]} />
          <Bar yAxisId="value" dataKey="arrears_value" fill="#ef4444" name="Arrears to Date Value" radius={[4, 4, 0, 0]} />
          <Bar yAxisId="lines" dataKey="incoming_lines" fill="#10b981" name="Total Incoming PO Lines" radius={[4, 4, 0, 0]} />
          <Bar yAxisId="lines" dataKey="arrears_lines" fill="#f59e0b" name="Arrears to Date PO Lines" radius={[4, 4, 0, 0]} />
        </ComposedChart>
      </ResponsiveContainer>
      </div>
    </ChartWrapper>
  )
}
